import React, { useState } from 'react';
import { AdminPageLayout } from '@/components/admin/shared/AdminPageLayout';
import { TimeRangeSelector } from '@/components/controls/TimeRangeSelector';
import { ServiceFilter } from '@/components/controls/ServiceFilter';
import { ComparisonToggle } from '@/components/controls/ComparisonToggle';
// import { MetricsGrid } from '@/components/bi/MetricsGrid'; // Removed - bi directory deleted
// import { TrendChart } from '@/components/bi/TrendChart'; // Removed - bi directory deleted
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { 
  DollarSign, 
  TrendingDown, 
  TrendingUp, 
  AlertTriangle, 
  Lightbulb,
  PieChart,
  BarChart3,
  Target,
  Download,
  Filter
} from 'lucide-react';

export function AdminCosts() {
  const [selectedTab, setSelectedTab] = useState('breakdown');

  // Mock data para KPIs de costos
  const costKPIs = [
    {
      id: 'total-cost',
      title: 'Costo Total',
      value: '$45,678',
      target: 50000,
      previousValue: 48120,
      unit: 'currency',
      status: 'success' as const,
      description: 'Costo total del mes',
      sparklineData: Array.from({ length: 30 }, (_, i) => ({
        value: 40000 + i * 200 + Math.sin(i * 0.2) * 2000 + Math.random() * 1000,
        timestamp: new Date(Date.now() - (29 - i) * 24 * 60 * 60 * 1000)
      }))
    },
    {
      id: 'cost-per-conversation',
      title: 'Costo por Conversación',
      value: '$0.75',
      target: 0.50,
      previousValue: 0.82,
      unit: 'currency',
      status: 'warning' as const,
      description: 'Costo promedio por interacción',
      sparklineData: Array.from({ length: 30 }, (_, i) => ({
        value: 0.70 + Math.sin(i * 0.3) * 0.15 + Math.random() * 0.1,
        timestamp: new Date(Date.now() - (29 - i) * 24 * 60 * 60 * 1000)
      }))
    },
    {
      id: 'efficiency-score',
      title: 'Eficiencia de Costos',
      value: '82%',
      target: 90,
      previousValue: 78,
      unit: 'percentage',
      status: 'warning' as const,
      description: 'Ratio costo/valor generado',
      sparklineData: Array.from({ length: 30 }, (_, i) => ({
        value: 75 + Math.sin(i * 0.1) * 8 + Math.random() * 5,
        timestamp: new Date(Date.now() - (29 - i) * 24 * 60 * 60 * 1000)
      }))
    },
    {
      id: 'savings-potential',
      title: 'Ahorro Potencial',
      value: '$8,450',
      target: 10000,
      previousValue: 7200,
      unit: 'currency',
      status: 'info' as const,
      description: 'Oportunidades de optimización',
      sparklineData: Array.from({ length: 30 }, (_, i) => ({
        value: 6000 + i * 100 + Math.random() * 500,
        timestamp: new Date(Date.now() - (29 - i) * 24 * 60 * 60 * 1000)
      }))
    }
  ];

  // Mock data para evolución de costos
  const costEvolutionData = Array.from({ length: 30 }, (_, i) => {
    const date = new Date();
    date.setDate(date.getDate() - (29 - i));
    return {
      timestamp: date,
      total: 35000 + i * 400 + Math.sin(i * 0.2) * 5000 + Math.random() * 2000,
      openai: 15000 + i * 200 + Math.sin(i * 0.3) * 2000 + Math.random() * 1000,
      infrastructure: 12000 + i * 100 + Math.random() * 800,
      claude: 5000 + i * 50 + Math.random() * 400,
      storage: 3000 + i * 50 + Math.random() * 200,
      label: date.toLocaleDateString()
    };
  });

  // Mock data para desglose de costos
  const costBreakdownData = [
    { 
      name: 'OpenAI', 
      value: 45, 
      amount: 20550, 
      color: '#00D4AA',
      trend: 8,
      description: 'GPT-4, GPT-3.5, Embeddings'
    },
    { 
      name: 'Infrastructure', 
      value: 30, 
      amount: 13700, 
      color: '#3B82F6',
      trend: -5,
      description: 'DigitalOcean, AWS, Networking'
    },
    { 
      name: 'Claude', 
      value: 15, 
      amount: 6850, 
      color: '#8B5CF6',
      trend: 12,
      description: 'Anthropic Claude API'
    },
    { 
      name: 'Storage & DB', 
      value: 10, 
      amount: 4578, 
      color: '#F59E0B',
      trend: 2,
      description: 'Supabase, File Storage'
    }
  ];

  // Mock data para optimizaciones
  const optimizationSuggestions = [
    {
      id: '1',
      title: 'Implementar Cache Inteligente',
      description: 'Reducir llamadas redundantes a OpenAI con un sistema de cache avanzado.',
      potentialSaving: 3200,
      complexity: 'medium',
      impact: 'high',
      timeframe: '2 semanas',
      category: 'technical'
    },
    {
      id: '2',
      title: 'Optimizar Prompts',
      description: 'Reducir longitud de prompts y mejorar eficiencia de tokens.',
      potentialSaving: 2100,
      complexity: 'low',
      impact: 'medium',
      timeframe: '1 semana',
      category: 'optimization'
    },
    {
      id: '3',
      title: 'Migrar a GPT-3.5 para Casos Simples',
      description: 'Usar modelo menos costoso para consultas que no requieren GPT-4.',
      potentialSaving: 4500,
      complexity: 'high',
      impact: 'high',
      timeframe: '4 semanas',
      category: 'architecture'
    },
    {
      id: '4',
      title: 'Consolidar Infraestructura',
      description: 'Optimizar uso de recursos de servidor y eliminar servicios redundantes.',
      potentialSaving: 1200,
      complexity: 'medium',
      impact: 'medium',
      timeframe: '3 semanas',
      category: 'infrastructure'
    }
  ];

  const getComplexityColor = (complexity: string) => {
    const colors = {
      low: 'bg-green-100 text-green-800',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-red-100 text-red-800'
    };
    return colors[complexity as keyof typeof colors] || colors.medium;
  };

  const getImpactColor = (impact: string) => {
    const colors = {
      low: 'bg-gray-100 text-gray-800',
      medium: 'bg-blue-100 text-blue-800',
      high: 'bg-purple-100 text-purple-800'
    };
    return colors[impact as keyof typeof colors] || colors.medium;
  };

  return (
    <AdminPageLayout
      title="Análisis de Costos"
      subtitle="Desglose detallado y oportunidades de optimización"
      actions={
        <>
          <Button variant="outline" size="sm">
            <Filter className="w-4 h-4 mr-2" />
            Filtros Avanzados
          </Button>
          <Button variant="outline" size="sm">
            <Download className="w-4 h-4 mr-2" />
            Exportar
          </Button>
        </>
      }
      filters={
        <>
          <TimeRangeSelector defaultValue="month" />
          <ServiceFilter multiple={true} />
          <ComparisonToggle />
        </>
      }
    >
      {/* KPIs de costos */}
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-green-600" />
            Métricas Financieras Clave
          </h2>
          {/* MetricsGrid removed - using simple grid instead */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {costKPIs.map((kpi) => (
              <div key={kpi.id} className="bg-white p-4 rounded-lg shadow">
                <h3 className="text-sm font-medium text-gray-500">{kpi.title}</h3>
                <p className="text-2xl font-bold">{kpi.value}</p>
                <Badge className={`mt-2 ${kpi.status === 'success' ? 'bg-green-100' : kpi.status === 'warning' ? 'bg-yellow-100' : 'bg-gray-100'}`}>
                  {kpi.status}
                </Badge>
              </div>
            ))}
          </div>
        </div>

        {/* Alertas de costos */}
        <Alert className="border-yellow-200 bg-yellow-50">
          <AlertTriangle className="h-4 w-4 text-yellow-600" />
          <AlertDescription className="text-yellow-800">
            <strong>Atención:</strong> Los costos de OpenAI han aumentado 8% este mes. 
            Se recomienda revisar las optimizaciones disponibles.
          </AlertDescription>
        </Alert>

        {/* Análisis detallado */}
        <Tabs value={selectedTab} onValueChange={setSelectedTab}>
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="breakdown" className="flex items-center gap-2">
              <PieChart className="h-4 w-4" />
              Desglose
            </TabsTrigger>
            <TabsTrigger value="trends" className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Tendencias
            </TabsTrigger>
            <TabsTrigger value="optimization" className="flex items-center gap-2">
              <Lightbulb className="h-4 w-4" />
              Optimización
            </TabsTrigger>
            <TabsTrigger value="comparison" className="flex items-center gap-2">
              <Target className="h-4 w-4" />
              Comparación
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="breakdown" className="mt-6">
            <div className="grid gap-6 lg:grid-cols-2">
              {/* Desglose por servicio */}
              <Card>
                <CardHeader>
                  <CardTitle>Distribución por Servicio</CardTitle>
                  <CardDescription>
                    Costos del mes actual por proveedor
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {costBreakdownData.map((item, index) => (
                    <div key={index} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-4 h-4 rounded-full"
                            style={{ backgroundColor: item.color }}
                          />
                          <div>
                            <span className="font-medium">{item.name}</span>
                            <p className="text-xs text-muted-foreground">{item.description}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">${item.amount.toLocaleString()}</span>
                            <div className="flex items-center gap-1">
                              {item.trend > 0 ? (
                                <TrendingUp className="h-3 w-3 text-red-600" />
                              ) : (
                                <TrendingDown className="h-3 w-3 text-green-600" />
                              )}
                              <span className={`text-xs ${item.trend > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                {item.trend > 0 ? '+' : ''}{item.trend}%
                              </span>
                            </div>
                          </div>
                          <div className="text-sm text-muted-foreground">{item.value}%</div>
                        </div>
                      </div>
                      <Progress value={item.value} className="h-2" />
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Desglose por cliente */}
              <Card>
                <CardHeader>
                  <CardTitle>Costos por Cliente</CardTitle>
                  <CardDescription>
                    Distribución de gastos por cliente
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {[
                      { name: 'BigCorp Enterprise', cost: 18500, percentage: 41 },
                      { name: 'TechCorp Solutions', cost: 12300, percentage: 27 },
                      { name: 'HealthFirst Medical', cost: 8900, percentage: 19 },
                      { name: 'StartupX Innovations', cost: 5978, percentage: 13 }
                    ].map((client, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <div>
                          <div className="font-medium">{client.name}</div>
                          <div className="text-sm text-muted-foreground">{client.percentage}% del total</div>
                        </div>
                        <div className="text-right">
                          <div className="font-medium">${client.cost.toLocaleString()}</div>
                          <Progress value={client.percentage} className="w-20 h-2 mt-1" />
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          <TabsContent value="trends" className="mt-6">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Evolución de Costos - Últimos 30 días</CardTitle>
                  <CardDescription>
                    Tendencia de gastos por servicio
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {/* TrendChart removed - showing simple placeholder */}
                  <div className="h-[400px] bg-gray-50 rounded flex items-center justify-center">
                    <p className="text-gray-500">Gráfico de tendencias (TrendChart component removed)</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          <TabsContent value="optimization" className="mt-6">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Lightbulb className="h-5 w-5 text-yellow-600" />
                    Oportunidades de Optimización
                  </CardTitle>
                  <CardDescription>
                    Ahorro potencial total: ${optimizationSuggestions.reduce((sum, opt) => sum + opt.potentialSaving, 0).toLocaleString()}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4">
                    {optimizationSuggestions.map((suggestion) => (
                      <div key={suggestion.id} className="p-4 border rounded-lg hover:bg-muted/30 transition-colors">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <h4 className="font-semibold mb-1">{suggestion.title}</h4>
                            <p className="text-sm text-muted-foreground mb-2">
                              {suggestion.description}
                            </p>
                          </div>
                          <div className="text-right ml-4">
                            <div className="text-lg font-bold text-green-600">
                              ${suggestion.potentialSaving.toLocaleString()}
                            </div>
                            <div className="text-xs text-muted-foreground">ahorro mensual</div>
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Badge className={getComplexityColor(suggestion.complexity)}>
                              {suggestion.complexity} complejidad
                            </Badge>
                            <Badge className={getImpactColor(suggestion.impact)}>
                              {suggestion.impact} impacto
                            </Badge>
                            <span className="text-sm text-muted-foreground">
                              ⏱️ {suggestion.timeframe}
                            </span>
                          </div>
                          
                          <Button size="sm">
                            Implementar
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="comparison" className="mt-6">
            <div className="grid gap-6 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle>Comparación Mensual</CardTitle>
                  <CardDescription>vs mes anterior</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {[
                      { metric: 'Costo Total', current: 45678, previous: 48120, target: 45000 },
                      { metric: 'Costo por Conversación', current: 0.75, previous: 0.82, target: 0.50 },
                      { metric: 'Eficiencia', current: 82, previous: 78, target: 90 },
                      { metric: 'ROI', current: 324, previous: 288, target: 300 }
                    ].map((item, index) => {
                      const isImprovement = item.current < item.previous || (item.metric === 'ROI' && item.current > item.previous);
                      const progress = item.metric === 'ROI' ? (item.current / item.target) * 100 : ((item.target - item.current) / item.target) * 100;
                      
                      return (
                        <div key={index} className="space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">{item.metric}</span>
                            <div className="flex items-center gap-2">
                              {isImprovement ? (
                                <TrendingUp className="h-4 w-4 text-green-600" />
                              ) : (
                                <TrendingDown className="h-4 w-4 text-red-600" />
                              )}
                              <span className={`text-sm ${isImprovement ? 'text-green-600' : 'text-red-600'}`}>
                                {item.metric === 'Costo por Conversación' ? 
                                  `$${item.current}` : 
                                  item.metric === 'Costo Total' ? 
                                  `$${item.current.toLocaleString()}` :
                                  `${item.current}${item.metric === 'ROI' || item.metric === 'Eficiencia' ? '%' : ''}`
                                }
                              </span>
                            </div>
                          </div>
                          <Progress 
                            value={Math.min(Math.max(progress, 0), 100)} 
                            className="h-2"
                          />
                          <div className="flex justify-between text-xs text-muted-foreground">
                            <span>
                              Anterior: {item.metric === 'Costo por Conversación' ? 
                                `$${item.previous}` : 
                                item.metric === 'Costo Total' ? 
                                `$${item.previous.toLocaleString()}` :
                                `${item.previous}${item.metric === 'ROI' || item.metric === 'Eficiencia' ? '%' : ''}`
                              }
                            </span>
                            <span>
                              Meta: {item.metric === 'Costo por Conversación' ? 
                                `$${item.target}` : 
                                item.metric === 'Costo Total' ? 
                                `$${item.target.toLocaleString()}` :
                                `${item.target}${item.metric === 'ROI' || item.metric === 'Eficiencia' ? '%' : ''}`
                              }
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Proyección Q4</CardTitle>
                  <CardDescription>Estimación basada en tendencias</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <h4 className="font-semibold text-blue-900 mb-2">Escenario Conservador</h4>
                      <p className="text-sm text-blue-800">
                        Manteniendo tendencias actuales: <strong>$52,000/mes</strong>
                      </p>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg">
                      <h4 className="font-semibold text-green-900 mb-2">Con Optimizaciones</h4>
                      <p className="text-sm text-green-800">
                        Implementando mejoras: <strong>$41,500/mes</strong>
                      </p>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-lg">
                      <h4 className="font-semibold text-purple-900 mb-2">Ahorro Anual</h4>
                      <p className="text-sm text-purple-800">
                        Potencial de ahorro: <strong>$126,000</strong>
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </AdminPageLayout>
  );
}